import { Component } from '@theme/component';
import { openAliaPopup, urlDiscountCode } from '@theme/alia-popup';

/** How long the "Copied!" confirmation stays on the chip. */
const COPIED_FEEDBACK_MS = 2000;

/**
 * The interactive half of `sections/sl-deals-banner.liquid`.
 *
 * Almost nothing is left to JS on purpose. The section decides in Liquid whether to paint the CTA
 * or the discount chip, because a logged-in customer already holds the code and rendering that
 * server-side avoids a flash of the wrong state above the fold.
 *
 * Exactly one case cannot be resolved in Liquid: the visitor who just came back from the Alia
 * sign-up with `?discount=CODE` in the URL. Liquid cannot read query parameters, so that swap
 * happens here.
 *
 * Deliberately does NOT use the `js-open-popup` / `js-discount-code` classes that
 * `assets/apply-discount-cards.js` drives globally: that script hardcodes the `DISCOUNT CODE: `
 * prefix, and its click handler would fire a second `alia.push` on top of this one.
 *
 * @typedef {{ cta: HTMLButtonElement, code: HTMLButtonElement, codeText: HTMLElement, copiedText: HTMLElement, announcer: HTMLElement }} Refs
 * @extends {Component<Refs>}
 */
class SlDealsBannerComponent extends Component {
  requiredRefs = ['cta', 'code', 'codeText', 'copiedText', 'announcer'];

  /** @type {() => void} */
  #cancelFallback = () => {};

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #copiedTimeout;

  /** @type {ResizeObserver | null} */
  #headerGroupObserver = null;

  connectedCallback() {
    super.connectedCallback();

    this.#revealCodeIfReturningFromSignup();
    this.#publishHeightInHeaderGroup();
  }

  /** Re-runs after a Section Rendering API re-render, which replaces the refs. */
  updatedCallback() {
    this.#revealCodeIfReturningFromSignup();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#cancelFallback();
    clearTimeout(this.#copiedTimeout);
    this.#headerGroupObserver?.disconnect();
    this.#headerGroupObserver = null;
  }

  /**
   * In the header group (sections/header-group.json) the bar sits BELOW the header, and every
   * section there counts towards `--header-group-height`. snippets/sl-search-modal.liquid places the
   * desktop search panel with `--header-group-height - --header-height`, which assumes the rest of
   * the group sits ABOVE the header (the announcement bar): with this bar in it, the panel opened
   * 57px too low and left a strip of the bar showing. It subtracts this variable to stay where it was.
   *
   * The size comes from the observer entry, never from a layout read.
   */
  #publishHeightInHeaderGroup() {
    const section = this.closest('#header-group > .shopify-section');
    if (!(section instanceof HTMLElement) || this.#headerGroupObserver) return;

    this.#headerGroupObserver = new ResizeObserver(([entry]) => {
      const height = Math.round(entry?.borderBoxSize[0]?.blockSize ?? 0);
      document.body.style.setProperty('--deals-banner-in-header-height', `${height}px`);
    });
    this.#headerGroupObserver.observe(section);
  }

  /** Bound declaratively with `on:click="/open"` on the CTA. */
  open() {
    this.#cancelFallback = openAliaPopup(this.dataset.campaignId);
  }

  /**
   * Bound with `on:click="/copyCode"` on the chip.
   *
   * `navigator.clipboard` needs a secure context and can be refused by permissions policy, so the
   * confirmation is shown only once the write actually resolves — never optimistically. On failure
   * the chip is left untouched: the code stays visible and selectable, which is still a usable
   * fallback, and the swallowed rejection is reported through console.error.
   */
  async copyCode() {
    const code = (this.dataset.code || '').trim();
    if (!code) return;

    try {
      await navigator.clipboard.writeText(code);
    } catch (error) {
      console.error('sl-deals-banner: clipboard write failed', error);
      return;
    }

    this.#showCopied();
  }

  /**
   * Reveals the confirmation, then restores the code.
   *
   * Only a class is toggled: both labels are already in the DOM, stacked in the same grid cell, so
   * the chip keeps its size and nothing reflows. The live region carries the announcement, since
   * the confirmation span itself is aria-hidden.
   */
  #showCopied() {
    const { code, copiedText, announcer } = this.refs;

    code.classList.add('sl-deals-banner__code--copied');
    announcer.textContent = copiedText.textContent;

    clearTimeout(this.#copiedTimeout);
    this.#copiedTimeout = setTimeout(() => {
      code.classList.remove('sl-deals-banner__code--copied');
      announcer.textContent = '';
    }, COPIED_FEEDBACK_MS);
  }

  /**
   * Swaps the CTA for the chip when the URL carries the code this banner is advertising.
   *
   * The codes are compared rather than merely checking that `?discount=` exists: arriving with a
   * different campaign's code must not reveal this banner's code.
   */
  #revealCodeIfReturningFromSignup() {
    const code = (this.dataset.code || '').trim().toUpperCase();
    if (!code || urlDiscountCode() !== code) return;

    this.refs.cta.hidden = true;
    this.refs.code.hidden = false;
    this.refs.announcer.textContent = this.refs.codeText.textContent;
  }
}

if (!customElements.get('sl-deals-banner-component')) {
  customElements.define('sl-deals-banner-component', SlDealsBannerComponent);
}
